import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';


// ─── Helpers ────────────────────────────────────────────────────────────────

function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

function getUserInitial() {
  const name =
    localStorage.getItem('user_name') ||
    localStorage.getItem('userName') ||
    '';
  return name.trim().charAt(0).toUpperCase() || null;
}

// EV-637 — admin detection from the JWT (same signal as BottomNav). Used to show
// the admin-only "add any venue to my dashboard" tool.
function getIsAdmin() {
  try {
    const tk = getAuthToken();
    if (!tk) return false;
    const payload = JSON.parse(atob((tk.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/')));
    if (String(payload?.role || '').toLowerCase() === 'admin') return true;
    return Array.isArray(payload?.roles) && payload.roles.some((r) => String(r).toLowerCase() === 'admin');
  } catch { return false; }
}

// Return an emoji that loosely represents the venue type
function venueEmoji(venueType) {
  if (!venueType) return '🏢';
  const t = venueType.toLowerCase();
  if (t.includes('cafe') || t.includes('coffee')) return '☕';
  if (t.includes('restaurant') || t.includes('food') || t.includes('dining')) return '🍽️';
  if (t.includes('bar') || t.includes('pub') || t.includes('club') || t.includes('nightlife')) return '🍸';
  if (t.includes('hotel') || t.includes('resort') || t.includes('accommodation') || t.includes('hostel')) return '🏨';
  if (t.includes('gym') || t.includes('fitness') || t.includes('sport')) return '🏋️';
  if (t.includes('spa') || t.includes('salon') || t.includes('beauty')) return '💆';
  if (t.includes('shop') || t.includes('store') || t.includes('market') || t.includes('retail')) return '🛍️';
  if (t.includes('office') || t.includes('cowork')) return '💼';
  if (t.includes('hospital') || t.includes('clinic') || t.includes('medical')) return '🏥';
  if (t.includes('school') || t.includes('university') || t.includes('education')) return '🎓';
  if (t.includes('park') || t.includes('garden') || t.includes('outdoor')) return '🌿';
  if (t.includes('temple') || t.includes('church') || t.includes('mosque') || t.includes('shrine')) return '🛕';
  return '🏠';
}

const ROLE_STYLES = {
  owner:   { background: '#dcfce7', color: '#166534' },
  manager: { background: '#dbeafe', color: '#1e40af' },
  staff:   { background: '#f3f4f6', color: '#374151' },
};

function roleBadgeStyle(role) {
  return ROLE_STYLES[role] || ROLE_STYLES.staff;
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{
      width: '2.5rem',
      height: '2.5rem',
      border: '3px solid #fed7aa',
      borderTop: '3px solid #f97316',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto'
    }} />
  );
}

function ChevronRight() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#9ca3af"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function BackArrow() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const MyVenuesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [venues, setVenues] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'empty' | 'error-auth' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // EV-637 — admin self-serve "add any venue to my dashboard" (for testing offers etc.)
  const isAdmin = getIsAdmin();
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const searchVenuesAdmin = async (term) => {
    setQ(term);
    if (!term.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const token = getAuthToken();
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/curator/venues/search?q=${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      setResults(Array.isArray(data.results) ? data.results : []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const addVenueToMine = async (venueId) => {
    setAddingId(venueId);
    try {
      const token = getAuthToken();
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/my-venues/${venueId}/self-grant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setShowAdd(false); setQ(''); setResults([]);
        await fetchVenues();
      } else {
        alert(data.message || t('vd.failedToAddVenue'));
      }
    } catch { alert(t('vd.failedToAddVenue')); }
    finally { setAddingId(null); }
  };

  const fetchVenues = async () => {
    setStatus('loading');
    setErrorMessage('');

    const token = getAuthToken();
    if (!token) {
      setStatus('error-auth');
      return;
    }

    try {
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/my-venues`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.status === 401) {
        setStatus('error-auth');
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || t('vd.serverErrorWithCode', { code: res.status }));
      }

      const data = await res.json();
      const list = Array.isArray(data.venues) ? data.venues : [];
      setVenues(list);
      setStatus(list.length === 0 ? 'empty' : 'ready');
    } catch (err) {
      console.error('[MyVenuesPage] fetch error:', err);
      setErrorMessage(err.message || t('vd.somethingWentWrongTryAgain'));
      setStatus('error');
    }
  };

  useEffect(() => {
    fetchVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userInitial = getUserInitial();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* ── Spin keyframe ── */}
      <style>{`
        @keyframes spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* ── Header bar ── */}
      <div style={{
        height: 64,
        background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
      }}>
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          aria-label={t('vd.backToMap')}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 10,
            width: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0
          }}
        >
          <BackArrow />
        </button>

        {/* Title */}
        <span style={{
          color: 'white',
          fontWeight: 700,
          fontSize: 18,
          letterSpacing: '-0.3px'
        }}>
          {t('vd.myVenues')}
        </span>

        {/* Right: user avatar initial or empty placeholder */}
        {userInitial ? (
          <div style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.25)',
            border: '1.5px solid rgba(255,255,255,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 700,
            fontSize: 16,
            flexShrink: 0
          }}>
            {userInitial}
          </div>
        ) : (
          <div style={{ width: 38 }} />
        )}
      </div>

      {/* ── Body ── */}
      <div style={{
        flex: 1,
        padding: '20px 16px',
        maxWidth: 600,
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box'
      }}>

        {/* EV-637 — admin-only: add any venue to my dashboard for testing */}
        {isAdmin && (
          <button
            onClick={() => setShowAdd(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', marginBottom: 16, padding: '12px 16px',
              background: '#fff', color: '#c2410c',
              border: '1.5px dashed #fb923c', borderRadius: 14,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            ➕ {t('vd.addVenueToMyDashboard')}
            <span style={{ fontSize: 11, fontWeight: 800, color: '#9a3412', background: '#ffedd5', borderRadius: 8, padding: '2px 7px' }}>{t('vd.adminUpper')}</span>
          </button>
        )}

        {/* Loading */}
        {status === 'loading' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 80,
            gap: 16
          }}>
            <Spinner />
            <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>{t('vd.loadingVenues')}</p>
          </div>
        )}

        {/* Error: not authenticated */}
        {status === 'error-auth' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 80,
            gap: 12,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 48, marginBottom: 4 }}>🔒</div>
            <p style={{ color: '#374151', fontWeight: 600, fontSize: 16, margin: 0 }}>
              {t('vd.pleaseLogInFirst')}
            </p>
            <p style={{ color: '#9ca3af', fontSize: 14, margin: '0 0 16px' }}>
              {t('vd.needLoggedInVenues')}
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '12px 28px',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.35)'
              }}
            >
              {t('vd.logIn')}
            </button>
          </div>
        )}

        {/* Error: other */}
        {status === 'error' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 80,
            gap: 12,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 48, marginBottom: 4 }}>⚠️</div>
            <p style={{ color: '#374151', fontWeight: 600, fontSize: 16, margin: 0 }}>
              {t('vd.somethingWentWrong')}
            </p>
            <p style={{ color: '#9ca3af', fontSize: 14, margin: '0 0 16px', maxWidth: 280 }}>
              {errorMessage}
            </p>
            <button
              onClick={fetchVenues}
              style={{
                padding: '12px 28px',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.35)'
              }}
            >
              {t('vd.tryAgain')}
            </button>
          </div>
        )}

        {/* Empty state */}
        {status === 'empty' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 80,
            textAlign: 'center',
            gap: 8
          }}>
            <div style={{ fontSize: 56, marginBottom: 8 }}>🏢</div>
            <p style={{ color: '#374151', fontWeight: 700, fontSize: 18, margin: 0 }}>
              {t('vd.noVenuesYet')}
            </p>
            <p style={{ color: '#9ca3af', fontSize: 14, margin: '8px 0 0', maxWidth: 280, lineHeight: 1.6 }}>
              {t('vd.notAddedToAnyVenue')}<br />
              {t('vd.askVenueManagerForInviteQr')}
            </p>
          </div>
        )}

        {/* Venue list */}
        {status === 'ready' && venues.map((venue) => {
          const badgeStyle = roleBadgeStyle(venue.role);
          const emoji = venueEmoji(venue.venueType);
          const rating = venue.rating ? parseFloat(venue.rating).toFixed(1) : null;

          return (
            <button
              key={venue.id || venue.slug}
              onClick={() => navigate('/venue-dashboard/' + venue.slug)}
              aria-label={t('vd.openDashboardFor', { name: venue.name })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                background: 'white',
                border: '1px solid #f3f4f6',
                borderRadius: 16,
                padding: 16,
                marginBottom: 12,
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                boxSizing: 'border-box',
                transition: 'box-shadow 0.15s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
            >
              {/* Left: emoji icon */}
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: '#fff7ed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                flexShrink: 0
              }}>
                {emoji}
              </div>

              {/* Center: text block */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 3
                }}>
                  {/* Venue name */}
                  <span style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: '#111827',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {venue.name}
                  </span>

                  {/* Role badge */}
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    ...badgeStyle
                  }}>
                    {capitalize(venue.role) || t('vd.staff')}
                  </span>
                </div>

                {/* City + venue type */}
                <p style={{
                  color: '#6b7280',
                  fontSize: 13,
                  margin: '0 0 4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {[venue.city, venue.venueType].filter(Boolean).join(' • ')}
                </p>

                {/* Rating */}
                {rating && (
                  <span style={{
                    color: '#f59e0b',
                    fontSize: 13,
                    fontWeight: 600
                  }}>
                    ★ {rating}
                  </span>
                )}
              </div>

              {/* Right: chevron */}
              <ChevronRight />
            </button>
          );
        })}
      </div>

      {/* EV-637 — admin venue picker modal */}
      {showAdd && (
        <div
          onClick={() => setShowAdd(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', width: '100%', maxWidth: 600, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 18, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{t('vd.addVenueToMyDashboard')}</span>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ca3af', cursor: 'pointer' }}>✕</button>
            </div>
            <input
              autoFocus
              value={q}
              onChange={(e) => searchVenuesAdmin(e.target.value)}
              placeholder={t('vd.searchVenuesByName')}
              style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 15, outline: 'none', marginBottom: 12 }}
            />
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {searching && <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 12 }}>{t('vd.searching')}</p>}
              {!searching && q.trim() && results.length === 0 && (
                <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 12 }}>{t('vd.noVenuesFound')}</p>
              )}
              {results.map((v) => (
                <button
                  key={v.id}
                  onClick={() => addVenueToMine(v.id)}
                  disabled={addingId === v.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 12, padding: 12, marginBottom: 8, cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 20 }}>🏢</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.venue_name}</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#9ca3af' }}>#{v.id} · {v.slug}</span>
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#c2410c' }}>{addingId === v.id ? '…' : `+ ${t('vd.add')}`}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <DashboardNavBar />
    </div>
  );
};

export default MyVenuesPage;
